import { useEffect, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, ShieldCheck } from 'lucide-react';

type Mode =
  | { kind: 'loading' }
  | { kind: 'verify'; factorId: string }
  | { kind: 'enroll'; factorId: string; qr: string; secret: string }
  | { kind: 'failed'; message: string };

const FRIENDLY_NAME = 'H-Plate portal';

function codeError(message: string | undefined): string {
  if (message && /invalid|expired|code/i.test(message)) {
    return 'That code did not match. Use the newest code in the app, and check your phone sets its time automatically.';
  }
  return message || 'Could not check the code. Try again.';
}

/**
 * Second sign-in step for every admin account. First time: set up an
 * authenticator app from a QR code. After that: type the 6-digit code at
 * sign-in and again whenever the last code is more than 12 hours old.
 */
export default function TwoStepSignIn() {
  const { user, displayName, isOwner, refreshAccess, signOut } = useAdminAuth();
  const [mode, setMode] = useState<Mode>({ kind: 'loading' });
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    (async () => {
      const { data, error: listError } = await supabase.auth.mfa.listFactors();
      if (listError) {
        setMode({ kind: 'failed', message: listError.message });
        return;
      }
      const verified = data.totp[0];
      if (verified) {
        setMode({ kind: 'verify', factorId: verified.id });
        return;
      }
      for (const stale of data.all.filter((f) => f.status !== 'verified')) {
        await supabase.auth.mfa.unenroll({ factorId: stale.id });
      }
      const { data: enrolled, error: enrollError } = await supabase.auth.mfa.enroll({
        factorType: 'totp',
        friendlyName: FRIENDLY_NAME,
        issuer: 'H-Plate',
      });
      if (enrollError || !enrolled) {
        setMode({ kind: 'failed', message: enrollError?.message ?? 'Could not start the set-up.' });
        return;
      }
      const qr = enrolled.totp.qr_code.startsWith('data:')
        ? enrolled.totp.qr_code
        : `data:image/svg+xml;utf-8,${enrolled.totp.qr_code}`;
      setMode({ kind: 'enroll', factorId: enrolled.id, qr, secret: enrolled.totp.secret });
    })();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (mode.kind !== 'verify' && mode.kind !== 'enroll') return;
    const clean = code.replace(/\s+/g, '');
    if (!/^\d{6}$/.test(clean)) {
      setError('Enter the 6-digit code from the app.');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const { error: verifyError } = await supabase.auth.mfa.challengeAndVerify({
        factorId: mode.factorId,
        code: clean,
      });
      if (verifyError) {
        setError(codeError(verifyError.message));
        return;
      }
      await refreshAccess();
    } finally {
      setSubmitting(false);
    }
  };

  const recovery = isOwner
    ? 'If you lose your phone, the authenticator can be removed from the Supabase dashboard so you can set it up again.'
    : 'If you lose your phone, ask a director to reset your two-step sign-in.';

  return (
    <div className="min-h-screen bg-[#070b13] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src="/logo-transparent.png" alt="H-Plate" className="w-16 h-16 mx-auto mb-4 object-contain logo-glow" />
          <h1 className="font-display text-2xl text-white tracking-tight">
            {mode.kind === 'enroll' ? 'Protect your account' : `Welcome back${displayName ? `, ${displayName.split(' ')[0]}` : ''}`}
          </h1>
          <p className="text-sm text-gray-400 mt-1">{user?.email}</p>
        </div>
        <Card className="border-white/10 shadow-2xl" style={{ background: 'rgba(17,24,39,0.7)' }}>
          <CardHeader className="pb-4">
            <CardTitle className="text-white text-lg flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-emerald-300" />
              {mode.kind === 'enroll' ? 'Set up two-step sign-in' : 'Enter your code'}
            </CardTitle>
            <CardDescription className="text-gray-400">
              {mode.kind === 'enroll'
                ? 'Every portal account needs a code from an authenticator app on your phone, as well as your password. This keeps the portal safe even if a password leaks.'
                : 'Open the authenticator app on your phone and type the 6-digit code for H-Plate. You are asked again every 12 hours.'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {mode.kind === 'loading' && (
              <div className="flex justify-center py-6">
                <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
              </div>
            )}

            {mode.kind === 'failed' && (
              <div className="space-y-4">
                <div className="p-3 rounded-lg bg-rose-500/10 border border-rose-500/25 text-rose-300 text-sm">
                  {mode.message}
                </div>
                <Button onClick={() => window.location.reload()} className="w-full bg-gray-800 hover:bg-gray-700 text-white">
                  Try again
                </Button>
              </div>
            )}

            {(mode.kind === 'verify' || mode.kind === 'enroll') && (
              <form onSubmit={handleSubmit} className="space-y-4">
                {mode.kind === 'enroll' && (
                  <ol className="space-y-4 text-sm text-gray-300">
                    <li>
                      <span className="text-white font-medium">1. Get an authenticator app.</span>{' '}
                      On iPhone the built-in Passwords app works. Google Authenticator and Microsoft Authenticator work on any phone.
                    </li>
                    <li className="space-y-3">
                      <span className="text-white font-medium block">2. Scan this code with the app.</span>
                      <div className="bg-white rounded-xl p-3 w-48 h-48 mx-auto">
                        <img src={mode.qr} alt="QR code for your authenticator app" className="w-full h-full" />
                      </div>
                      <p className="text-xs text-gray-400">
                        Cannot scan? Add an account by hand with this key:
                        <span className="block mt-1 font-mono text-gray-200 break-all select-all">
                          {mode.secret.match(/.{1,4}/g)?.join(' ')}
                        </span>
                      </p>
                    </li>
                    <li>
                      <span className="text-white font-medium">3. Type the 6-digit code the app shows.</span>
                    </li>
                  </ol>
                )}

                {error && (
                  <div className="p-3 rounded-lg bg-rose-500/10 border border-rose-500/25 text-rose-300 text-sm">
                    {error}
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="two-step-code" className="text-gray-300">6-digit code</Label>
                  <Input
                    id="two-step-code"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    autoFocus
                    maxLength={7}
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/[^\d ]/g, ''))}
                    placeholder="123456"
                    className="bg-white/[0.04] border-white/10 text-white placeholder:text-gray-600 tracking-[0.3em] text-center text-lg"
                    required
                  />
                </div>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full text-white font-semibold border-0"
                  style={{ background: 'linear-gradient(135deg, #06b6d4 0%, #10b981 100%)' }}
                >
                  {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  {mode.kind === 'enroll' ? 'Turn on and continue' : 'Continue'}
                </Button>
                <p className="text-xs text-gray-500">{recovery}</p>
                <button type="button" onClick={() => void signOut()} className="w-full text-xs text-gray-500 hover:text-gray-300">
                  Sign out
                </button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
